/**
 * Thin wrapper around the browser-native Web Speech API. Keeps all the
 * ceremony (vendor prefix, config, event plumbing) out of components.
 *
 * We intentionally do NOT use any third-party STT SaaS — Web Speech runs
 * on-device (or via the browser vendor's own service), keeps latency low,
 * cost zero, and PII off our servers.
 */

const VOICE_LANG_KEY = "voice_lang";
export const DEFAULT_LANG = "en-IN";
export const SUPPORTED_LANGS = [
    { code: "en-IN", label: "English", short: "EN" },
    { code: "ml-IN", label: "മലയാളം", short: "മ" },
];

/** True when the browser exposes SpeechRecognition (Chrome/Edge/Opera). */
export function isSpeechRecognitionSupported() {
    if (typeof window === "undefined") return false;
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function getStoredLang() {
    try {
        const v = localStorage.getItem(VOICE_LANG_KEY);
        if (v && SUPPORTED_LANGS.some((l) => l.code === v)) return v;
    } catch { /* ignore */ }
    return DEFAULT_LANG;
}

export function setStoredLang(lang) {
    try {
        if (SUPPORTED_LANGS.some((l) => l.code === lang)) {
            localStorage.setItem(VOICE_LANG_KEY, lang);
        }
    } catch { /* ignore */ }
}

/**
 * Instantiate a SpeechRecognition object with the given callbacks.
 * Returns `{ start, stop, changeLang, active }` where `active` is a getter.
 *
 * The recogniser stops automatically on either:
 *   - the browser's own end-of-speech detection, OR
 *   - a 1.5s silence timer we set after the last interim result.
 */
export function createRecognizer({ lang, onInterim, onFinal, onError, onEnd }) {
    if (!isSpeechRecognitionSupported()) {
        throw new Error("Speech recognition not supported in this browser");
    }
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Ctor();
    rec.lang = lang || getStoredLang();
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 3;

    let silenceTimer = null;
    let manualStop = false;
    let bestFinal = "";
    let latestInterim = "";

    const resetSilenceTimer = () => {
        if (silenceTimer) clearTimeout(silenceTimer);
        silenceTimer = setTimeout(() => {
            // No sound for 1.5s — stop and let onFinal fire.
            try { rec.stop(); } catch { /* ignore */ }
        }, 1500);
    };

    rec.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
            const res = event.results[i];
            const text = res[0]?.transcript || "";
            if (res.isFinal) {
                bestFinal = (bestFinal + " " + text).trim();
            } else {
                interim = (interim + " " + text).trim();
            }
        }
        latestInterim = interim;
        onInterim?.(interim || bestFinal);
        resetSilenceTimer();
    };

    rec.onerror = (e) => {
        onError?.(e?.error || "unknown");
    };

    rec.onspeechstart = () => resetSilenceTimer();
    rec.onaudiostart = () => resetSilenceTimer();

    rec.onend = () => {
        if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
        const finalText = (bestFinal || latestInterim).trim();
        onFinal?.(finalText);
        onEnd?.(finalText, { manualStop });
    };

    return {
        start() {
            manualStop = false;
            bestFinal = "";
            latestInterim = "";
            try { rec.start(); } catch (err) { onError?.(err?.message || "start_failed"); }
        },
        stop() {
            manualStop = true;
            try { rec.stop(); } catch { /* ignore */ }
        },
        changeLang(nextLang) {
            // SpeechRecognition doesn't let us change lang mid-stream — the
            // caller must stop() then start() the next capture cycle.
            rec.lang = nextLang || DEFAULT_LANG;
        },
    };
}
